"use client";

import { Search } from "lucide-react";

import { KeyCombo } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { getTone } from "@/lib/ui/tones";
import { searchKeys } from "@/lib/keyboard";
import { cn } from "@/lib/utils";

/** The focus ring every search control in the nav shows. */
export const SEARCH_FOCUS_RING =
  "transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-800 focus-visible:ring-offset-2";

/**
 * The coral tile around a search control's icon: the 36px trigger on the
 * rail, and the back arrow inside the open field. The caller sets its size.
 */
export const SEARCH_TILE = cn(
  "inline-flex shrink-0 items-center justify-center hover:bg-brand-100 hover:text-brand-700",
  getTone("brand").tile,
  SEARCH_FOCUS_RING,
);

// The coral 36px tile, or, with `wide`, a field across the nav: the icon,
// what search covers and the keys that open it. Both open the same search;
// only the panel's state picks between them.
const TILE_TRIGGER = cn("h-9 w-9 rounded-xl", SEARCH_TILE);
const FIELD_TRIGGER = cn(
  "flex h-9 w-full items-center gap-2.5 rounded-xl border border-stone-200/70 bg-white px-3 text-left text-sm text-gray-400 hover:border-stone-300 hover:text-gray-500",
  SEARCH_FOCUS_RING,
);

/** The button that opens search; the tooltip names the keys. */
export default function NavSearchTrigger({
  wide = false,
  buttonRef,
  tooltipSide,
  onClick,
  className,
}) {
  const apple = useApplePlatform();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          onClick={onClick}
          aria-label="Search"
          className={cn(wide ? FIELD_TRIGGER : TILE_TRIGGER, className)}
        >
          <Search className={cn("h-4 w-4 shrink-0", wide && "text-brand-600")} />
          {wide && (
            <>
              <span className="truncate">Search pages, people, content…</span>
              <KeyCombo keys={searchKeys(apple)} className="ml-auto" />
            </>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>
        {apple
          ? "Hold Command and press K to open search."
          : "Hold Ctrl and press K to open search."}
      </TooltipContent>
    </Tooltip>
  );
}
